import type { ReactNode } from "react";

import { Skeleton } from "@/components/ui/skeleton";

import { cn } from "@/lib/utils";

export interface MoneySplitFigure {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
}

interface MoneySplitHeroProps {
  /** Bosh raqamning nomi ("Platforma sof daromadi", "O'qituvchi daromadi"). */
  label: string;
  value: ReactNode;
  /** Bosh raqam qanday hisoblanganini bir jumlada aytadi. */
  formula?: ReactNode;
  /** O'ngdagi yordamchi raqamlar: aylanma, ikkinchi tomon ulushi. */
  figures: MoneySplitFigure[];
  /**
   * Aylanmaning bo'linishi. `highlight` — bosh raqamga tegishli bo'lak
   * (chiziqda to'q rangda chiziladi).
   */
  split?: {
    platformUzs: number;
    teacherUzs: number;
    platformLabel: string;
    teacherLabel: string;
    highlight: "platform" | "teacher";
  };
  note?: ReactNode;
  isLoading?: boolean;
}

const percentOf = (part: number, whole: number): number =>
  whole > 0 ? Math.round((Math.max(part, 0) / whole) * 1000) / 10 : 0;

/**
 * Pulning bosh bloki: kim NIMA oladi.
 *
 * Ilgari analitika va o'qituvchi hisobotining bosh raqami "sof daromad"
 * deb nomlangan, lekin aslida SOTUV AYLANMASI edi (olingan − qaytarilgan):
 * platforma ham, o'qituvchi ham o'zinikini emas, hammaning pulini ko'rardi.
 * Endi bosh raqam — shu sahifa egasining haqiqiy ulushi, aylanma va
 * ikkinchi tomon ulushi esa yonida, bo'linish chizig'i bilan.
 */
export const MoneySplitHero = ({
  label,
  value,
  formula,
  figures,
  split,
  note,
  isLoading = false,
}: MoneySplitHeroProps) => {
  const total = split ? Math.max(split.platformUzs + split.teacherUzs, 0) : 0;
  const platformPercent = split ? percentOf(split.platformUzs, total) : 0;
  const teacherPercent = split ? Math.round((100 - platformPercent) * 10) / 10 : 0;
  const segments = split
    ? [
        {
          key: "platform",
          label: split.platformLabel,
          percent: platformPercent,
          strong: split.highlight === "platform",
        },
        {
          key: "teacher",
          label: split.teacherLabel,
          percent: teacherPercent,
          strong: split.highlight === "teacher",
        },
      ]
    : [];
  // Bosh raqamga tegishli bo'lak chiziqda birinchi turadi.
  if (split?.highlight === "teacher") segments.reverse();

  return (
    <section className="border-border bg-brand-panel text-brand-panel-foreground overflow-hidden rounded-2xl border">
      <div className="grid gap-6 p-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-2">
          <p className="eyebrow opacity-80">{label}</p>
          {isLoading ? (
            <Skeleton className="h-12 w-56 opacity-40" />
          ) : (
            <p className="font-heading text-4xl font-bold tracking-tight break-words sm:text-5xl">
              {value}
            </p>
          )}
          {formula && <p className="text-sm opacity-80">{formula}</p>}
        </div>

        <dl className="grid content-start gap-3 sm:grid-cols-2 lg:grid-cols-1">
          {figures.map(figure => (
            <div
              key={figure.label}
              className="rounded-xl border border-white/10 bg-white/5 px-4 py-3"
            >
              <dt className="text-xs font-medium tracking-wide uppercase opacity-70">
                {figure.label}
              </dt>
              <dd className="mt-1">
                {isLoading ? (
                  <Skeleton className="h-6 w-32 opacity-40" />
                ) : (
                  <span className="font-heading block text-xl font-semibold tabular-nums">
                    {figure.value}
                  </span>
                )}
                {figure.hint && (
                  <span className="mt-0.5 block text-xs opacity-70">{figure.hint}</span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      {split && (
        <div className="border-t border-white/10 px-6 py-4">
          <div
            className="flex h-2.5 overflow-hidden rounded-full bg-white/10"
            role="img"
            aria-label={segments.map(segment => `${segment.label} ${segment.percent}%`).join(", ")}
          >
            {total > 0 &&
              segments.map(segment => (
                <span
                  key={segment.key}
                  className={cn(
                    "h-full transition-[width]",
                    segment.strong ? "bg-brand-accent" : "bg-white/35",
                  )}
                  style={{ width: `${segment.percent}%` }}
                />
              ))}
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs">
            <span className="flex flex-wrap gap-x-4 gap-y-1">
              {segments.map(segment => (
                <span key={segment.key} className="inline-flex items-center gap-1.5">
                  <span
                    className={cn(
                      "size-2 rounded-full",
                      segment.strong ? "bg-brand-accent" : "bg-white/35",
                    )}
                    aria-hidden
                  />
                  <span className={cn(segment.strong ? "font-semibold" : "opacity-80")}>
                    {segment.label} · {total > 0 ? `${segment.percent}%` : "—"}
                  </span>
                </span>
              ))}
            </span>
            {note && <span className="opacity-70">{note}</span>}
          </div>
        </div>
      )}
    </section>
  );
};
